import { Radio } from '@fountain/fountain-ui-components';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Chip,
  Grid,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { WorkflowComplianceStage } from 'api-clients/monolith';
import produce from 'immer';
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { makeUniversalRecruiterDashboardEnabled } from 'containers/Auth_old/selectors';
import { StageSettingCard } from 'containers/WorkflowEditor/components';
import { IdleMoveRule } from 'containers/WorkflowEditor/components/IdleMoveRule';
import { ReviewStage } from 'containers/WorkflowEditor/components/ReviewStage';
import { SettingSwitch } from 'containers/WorkflowEditor/components/WorkflowEditorContent/SettingSwitch';
import { Rules } from 'containers/WorkflowEditor/components/Rules';
import { StageSection } from 'containers/WorkflowEditor/components/StageSection';
import { StageSettingMessageCard } from 'containers/WorkflowEditor/components/StageSettingMessageCard';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';
import {
  ComplianceRequirement,
  useComplianceRequirements,
} from 'hooks/useComplianceRequirements';

import { messages } from './messages';
import { useStyles } from './styles';

export interface ComplianceStageProps {
  stage: WorkflowComplianceStage;
}

type AutoAdvanceOption = 'none' | 'on_completion' | 'on_submission';

// `configured_requirement_uuids` isn't in the generated `extra` type yet;
// intersect it in so we get real typing instead of `any`/`unknown`.
type StageExtraWithConfiguredRequirementUuids =
  WorkflowComplianceStage['extra'] & {
    configured_requirement_uuids?: string[];
  };

function ComplianceStageSettings({ stage }: ComplianceStageProps) {
  const styles = useStyles();
  const intl = useIntl();
  const { setStage } = useContext(StageContext);

  const { requirements, loading: requirementsLoading } =
    useComplianceRequirements();

  const getSelectedOption = (): AutoAdvanceOption => {
    if (stage.extra.auto_advance_on_completion) return 'on_completion';
    if (stage.extra.auto_advance_on_submission) return 'on_submission';
    return 'none';
  };
  const selectedOption = getSelectedOption();

  const configuredUuids: string[] =
    (stage.extra as StageExtraWithConfiguredRequirementUuids)
      .configured_requirement_uuids ?? [];

  const selectedRequirements = requirements.filter(req =>
    configuredUuids.includes(req.uuid),
  );

  const onSelectOption = (option: AutoAdvanceOption) => {
    setStage(
      produce(stage, draftStage => {
        draftStage.extra.auto_advance_on_completion =
          option === 'on_completion';
        draftStage.extra.auto_advance_on_submission =
          option === 'on_submission';
      }),
    );
  };

  const onRequirementsChange = (selected: ComplianceRequirement[]) => {
    setStage(
      produce(stage, draftStage => {
        (
          draftStage.extra as StageExtraWithConfiguredRequirementUuids
        ).configured_requirement_uuids = selected.map(req => req.uuid);
      }),
    );
  };

  const onToggleExcludeInternalRequirements = () => {
    setStage(
      produce(stage, draftStage => {
        draftStage.extra.exclude_internal_requirements_from_advancement =
          !draftStage.extra.exclude_internal_requirements_from_advancement;
      }),
    );
  };

  return (
    <StageSettingCard
      title={<FormattedMessage {...messages.complianceStageSettings} />}
      isLoading={false}
      variant="default"
    >
      <Grid container direction="column">
        <Grid item>
          <Typography variant="body2" className={styles.radioGroupLabel}>
            <FormattedMessage {...messages.autoAdvanceLabel} />
          </Typography>
        </Grid>
        <RadioGroup
          name="auto-advance"
          value={selectedOption}
          className={styles.radioOptionsContainer}
        >
          <Grid item>
            <Radio
              labelComponent={
                <Typography variant="body2" className={styles.radioLabel}>
                  <FormattedMessage {...messages.moveApplicant} />
                </Typography>
              }
              checked={selectedOption === 'on_completion'}
              onChange={() => onSelectOption('on_completion')}
            />
          </Grid>
          <Grid item>
            <Radio
              labelComponent={
                <Typography variant="body2" className={styles.radioLabel}>
                  <FormattedMessage {...messages.moveApplicantOnSubmission} />
                </Typography>
              }
              checked={selectedOption === 'on_submission'}
              onChange={() => onSelectOption('on_submission')}
            />
          </Grid>
          <Grid item>
            <Radio
              labelComponent={
                <Typography variant="body2" className={styles.radioLabel}>
                  <FormattedMessage {...messages.noAutoAdvance} />
                </Typography>
              }
              checked={selectedOption === 'none'}
              onChange={() => onSelectOption('none')}
            />
          </Grid>
        </RadioGroup>
        <Grid item style={{ marginTop: 16 }}>
          <Typography variant="body2" className={styles.radioGroupLabel}>
            <FormattedMessage {...messages.requirementsLabel} />
          </Typography>
          <Autocomplete
            multiple
            loading={requirementsLoading}
            options={requirements}
            value={selectedRequirements}
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.uuid === value.uuid}
            onChange={(_event, selected) => onRequirementsChange(selected)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.uuid}
                  label={option.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={
                  selectedRequirements.length === 0
                    ? intl.formatMessage(messages.requirementsPlaceholder)
                    : undefined
                }
                helperText={
                  <FormattedMessage {...messages.requirementsHelperText} />
                }
              />
            )}
          />
          <SettingSwitch
            dataKey="exclude_internal_requirements_from_advancement"
            checked={Boolean(
              stage.extra.exclude_internal_requirements_from_advancement,
            )}
            // Only "When all documents are submitted" reads this setting
            // (ComplianceStage#documents_submitted?); "When applicant is in
            // compliance" deliberately keeps counting internal requirements
            // exactly as it does today (CMP-1607 is scoped to submission-only).
            // Disabled elsewhere so the switch can't promise something it
            // does not do.
            disabled={selectedOption !== 'on_submission'}
            onChange={onToggleExcludeInternalRequirements}
            label={
              <FormattedMessage
                {...messages.excludeInternalRequirementsLabel}
              />
            }
            tooltipDescription={intl.formatMessage(
              messages.excludeInternalRequirementsTooltip,
            )}
          />
        </Grid>
      </Grid>
    </StageSettingCard>
  );
}

export default function ComplianceStage({ stage }: ComplianceStageProps) {
  const isSubstage = stage.parent_stage_id;
  const universalRecruiterDashboardEnabled = useSelector(
    makeUniversalRecruiterDashboardEnabled(),
  );

  return (
    <div data-testid="ComplianceStage">
      {universalRecruiterDashboardEnabled && (
        <StageSection>
          <ReviewStage stage={stage} />
        </StageSection>
      )}
      <StageSection>
        <ComplianceStageSettings stage={stage} />
      </StageSection>
      <StageSection>
        <Rules stage={stage} />
      </StageSection>
      {!isSubstage && (
        <>
          <StageSection>
            <IdleMoveRule stage={stage} />
          </StageSection>
          <StageSection>
            <StageSettingMessageCard
              messageType="automated"
              stageExternalId={stage.external_id}
            />
          </StageSection>
          <StageSection>
            <StageSettingMessageCard
              messageType="trigger"
              stageExternalId={stage.external_id}
            />
          </StageSection>
        </>
      )}
    </div>
  );
}
